import {
	Component,
	Output,
	EventEmitter,
	Input,
	Inject,
	LOCALE_ID,
	ChangeDetectionStrategy,
	ViewChild,
	OnChanges,
	SimpleChanges,
	ViewEncapsulation,
} from '@angular/core';
import { DynamicFormControl } from '../input/dynamic-form-control';
import { fill, isString, isNumber, toString, padStart } from 'lodash-es';
import { DatePipe, getLocaleDateFormat, FormatWidth } from '@angular/common';
import { toInt } from 'src/app/shared/utilities/number.utils';
import { guid } from 'src/app/shared/utilities/guid.utils';
import { LocaleService } from 'src/app/core/locale/locale.service';
import { DynamicFormDropdownOption } from '../input/dynamic-form-control.models';
import { validString, validArray } from 'src/app/shared/utilities/types.utils';
import { DynamicFormComponent } from '../dynamic-form.component';
import { calculateAge, getDateFromInput } from 'src/app/shared/utilities/date.utils';
import { normalize } from 'src/app/shared/utilities/string.utils';

interface SelectedValue {
	day: string;
	month: string;
	year: string;
	date: string;
}

interface DateCollectionFormChangedEvent {
	value: {
		day: string;
		month: string;
		year: string;
		date: string;
	};
}

export enum DateCollectionKeyEnum {
	year = 'year',
	month = 'month',
	day = 'day',
}

export interface DateCollectionChangedEvent {
	value: string;
	date: Date;
}

@Component({
	selector: 'ahp-date-collection',
	templateUrl: './date-collection.component.html',
	styleUrls: ['./date-collection.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class DateCollectionComponent implements OnChanges {
	/**
	 * Holds the date collector view child
	 */
	@ViewChild('dateCollector', { static: true }) dateCollector: DynamicFormComponent;
	/**
	 * Tells the component if the user clicked the submit button in the form.
	 */
	@Input() formSubmitted?: boolean;
	/**
	 * If this is a credit card expiration date collection, pass true.
	 */
	@Input() creditCardExpiration: boolean;
	/**
	 * This is the control for which we'll be binding the value to.
	 */
	@Input() control: DynamicFormControl<string>;
	/**
	 * If you want to require a minimum date, pass it in here.
	 */
	@Input() minDate?: string;
	/**
	 * If you want to require a maximum date, pass it in here.
	 */
	@Input() maxDate?: string;
	/**
	 * If you want to require a minimum age, pass it in here.
	 */
	@Input() minAge?: number;
	/**
	 * If you want to require a maximum age, pass it in here.
	 */
	@Input() maxAge?: number;
	/**
	 * Pass in as value YYYY-MM-DD
	 */
	@Input() value?: string;
	/**
	 * If you only want to show some of the parts
	 */
	@Input() use?: DateCollectionKeyEnum[];
	/**
	 * Allows you to format the output on a successful date selected.
	 * @see {@link https://angular.io/api/common/DatePipe}
	 */
	@Input() outputFormat: string;
	/**
	 * Used to be notified when the date has changed.
	 */
	@Output() dateChanged = new EventEmitter<string>();
	/**
	 * Once the user selects a date, this is the error that shows if it doesn't meet specific criteria (e.g. min date)
	 */
	error: string;
	/**
	 * Allows you to specify an element id that the section is labeled by.
	 */
	labelId = guid();
	/**
	 * Holds the layout for our display.
	 */
	controls: DynamicFormControl<any>[][];
	/**
	 * The year
	 */
	year = '';
	/**
	 * The month
	 */
	month = '';
	/**
	 * The day
	 */
	day = '';
	/**
	 * Holds the last values the user selected
	 */
	private lastValues: Partial<SelectedValue> = {};
	/**
	 * The date pipe for formatting.
	 */
	private datePipe: DatePipe;
	/**
	 * The format it should be in
	 */
	private format: string;

	/**
	 * @param localeID Our current locale id
	 * @param localeService The locale service
	 */
	constructor(@Inject(LOCALE_ID) private localeID, private localeService: LocaleService) {
		this.datePipe = new DatePipe(this.localeID);
		this.format = this.standardizeFormatString(getLocaleDateFormat(this.localeID, FormatWidth.Short));
	}

	/**
	 * Called when the component properties changed.
	 * @param changes The changed properties
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (!this.controls || changes.value?.currentValue) {
			const value: string = changes.value.currentValue;

			if (value === this.lastValues.date) {
				return;
			}

			if (validString(value)) {
				const [year, month, day] = value.split('-');
				this.year = year || '';
				this.month = month || '';
				this.day = day || '';
			}
			this.controls = this.getFormControls();
		}
	}

	/**
	 * Returns a standardized format to use.
	 * @param format The format to use
	 */
	standardizeFormatString(format: string): string {
		return format.replace(/[^mdy]/gi, '/');
	}

	/**
	 * Returns the fxFlex sizes to use with the select layouts
	 */
	getSizes(): { month?: number; day?: number; year?: number } {
		const sizes = {
			year: 30,
			month: 45,
			day: 25,
		};

		if (validArray(this.use)) {
			this.use.forEach((key) => {
				const len = this.use.length;
				switch (len) {
					case 2:
						sizes[key] = 50;
						break;
					case 1:
						sizes[key] = 100;
						break;
				}
			});
		}

		return sizes;
	}

	/**
	 * Returns the layout to be used with the component
	 */
	getFormControls(): DynamicFormControl<string>[][] {
		if (this.creditCardExpiration) {
			return this.getCreditCardExpirationLayout();
		} else {
			return this.getSelectLayout();
		}
	}

	/**
	 * Returns the single input layout
	 */
	getCreditCardExpirationLayout(): DynamicFormControl<string>[][] {
		return [
			[
				new DynamicFormControl<string>({
					type: 'tel',
					controlType: 'text',
					value: validString(this.month)
						? padStart(this.month, 2, '0') + '/' + (validString(this.year) ? this.year.substring(2) : '')
						: '',
					key: 'date',
					label: 'common.expirationdate',
					placeholder: 'creditcard.mmyy',
					validators: 'required,maxLength:5',
					transform: 'monthYear',
				}),
			],
		];
	}

	/**
	 * Returns the configured <select> layout
	 */
	getSelectLayout(): DynamicFormControl<string>[][] {
		const sizes = this.getSizes();
		const { localeService, datePipe, day, month, year } = this;
		const currentValues = this.lastValues || { year: '', month: '', day: '', date: '' };
		const dayValue = currentValues.day ? currentValues.day : toInt(day) > 0 ? '' + day : '';
		const yearValue = currentValues.year ? currentValues.year : toInt(year) > 0 ? '' + year : '';
		let monthValue = currentValues.month ? currentValues.month : isString(month) ? month : undefined;
		monthValue = isNaN(parseInt(monthValue, 10)) ? '' : monthValue;

		const dayDropdown = new DynamicFormControl<string>({
			controlType: 'select',
			value: dayValue,
			key: DateCollectionKeyEnum.day,
			size: sizes.day,
			sizeXs: sizes.day,
			label: 'common.day',
			options: fill(Array(31), null).map((ignore: null, index: number): DynamicFormDropdownOption => {
				const value = `${index + 1}`;
				return {
					label: value,
					value,
				};
			}),
			validators: 'required',
		});
		const monthDropdown = new DynamicFormControl<string>({
			controlType: 'select',
			value: monthValue,
			key: DateCollectionKeyEnum.month,
			label: 'common.month',
			size: sizes.month,
			sizeXs: sizes.month,
			options: fill(Array(12), null).map((ignore: null, index: number): DynamicFormDropdownOption => {
				const date = new Date();
				date.setDate(1);
				date.setMonth(index);

				return {
					label: datePipe.transform(date, 'MMMM'),
					value: `${index}`,
				};
			}),
			validators: 'required',
		});

		const yearDropdown = new DynamicFormControl<string>({
			controlType: 'select',
			value: yearValue,
			key: DateCollectionKeyEnum.year,
			label: 'common.year',
			size: sizes.year,
			sizeXs: sizes.year,
			options: this.getYears(),
			validators: 'required',
		});

		yearDropdown.options.unshift({
			label: localeService.get('common.year'),
			value: '',
		});
		monthDropdown.options.unshift({
			label: localeService.get('common.month'),
			value: '',
		});
		dayDropdown.options.unshift({
			label: localeService.get('common.day'),
			value: '',
		});

		let controls = this.format.split('/').map((part) => {
			switch (part) {
				case 'yy':
					return yearDropdown;
				case 'M':
					return monthDropdown;
				default:
					return dayDropdown;
			}
		});

		// If they only want to show certain ones...
		if (validArray(this.use)) {
			controls = controls.reduce((acc: DynamicFormControl<string>[], control: DynamicFormControl<string>) => {
				if (this.use.includes(control.key as DateCollectionKeyEnum)) {
					acc.push(control);
				}
				return acc;
			}, []);
		}

		return [controls];
	}

	/**
	 * Returns the options to use with years.
	 */
	getYears(): { label: string; value: string }[] {
		const currentYear = new Date().getFullYear();
		return fill(Array(150), null).map((ignore: null, index: number): DynamicFormDropdownOption => {
			const value = `${currentYear - index}`;

			return {
				label: value,
				value,
			};
		});
	}

	/**
	 * Returns the current year
	 */
	getCurrentYear(): number {
		return new Date().getFullYear();
	}

	/**
	 * Called when the form validity changes.
	 * @param changes If values changed
	 */
	formValidityChanged(changes: DateCollectionFormChangedEvent): void {
		let { year, month, day } = changes.value;
		const { date } = changes.value;
		this.lastValues = {
			day,
			month,
			year,
			date: '',
		};
		this.error = null;

		// When we're doing a single date a string
		if (this.creditCardExpiration) {
			if (validString(date)) {
				const [monthPart, yearPart] = date.split('/');
				const monthValidString = validString(monthPart);
				const yearValidString = validString(yearPart);

				if (monthValidString) {
					let setMonth = toInt(monthPart);
					if (setMonth > 0) {
						// JS months start at 0
						setMonth -= 1;
					}

					month = toString(setMonth);
				}

				if (yearValidString) {
					const setYear = toInt(yearPart);
					const currentYearString = toString(this.getCurrentYear());
					const currentYearPrefix = toInt(currentYearString.substring(0, 2));
					const currentYearSuffix = toInt(currentYearString.substring(2));

					if (setYear < currentYearSuffix && 100 - currentYearSuffix < 30) {
						year = `${currentYearPrefix + 1}${setYear}`;
					} else {
						year = `${currentYearPrefix}${setYear}`;
					}
				}

				if (monthValidString && yearValidString) {
					day = '1';
				}
			}
			// If we've been told to only use specific fields
		} else if (validArray(this.use)) {
			let valid = true;
			const today = new Date();

			this.use.forEach((key) => {
				switch (key) {
					case DateCollectionKeyEnum.year:
						valid = valid && validString(year);
						break;
					case DateCollectionKeyEnum.month:
						valid = valid && validString(month);
						break;
					case DateCollectionKeyEnum.day:
						valid = valid && validString(day);
						break;
				}
			});

			if (!valid) {
				this.dateChanged.emit('');
				return;
			}

			[DateCollectionKeyEnum.year, DateCollectionKeyEnum.month, DateCollectionKeyEnum.day].forEach((key) => {
				if (this.use.includes(key)) {
					return;
				}

				switch (key) {
					case DateCollectionKeyEnum.year:
						year = toString(today.getFullYear());
						break;
					case DateCollectionKeyEnum.month:
						month = toString(today.getMonth());
						break;
					case DateCollectionKeyEnum.day:
						day = '1';
						break;
				}
			});
		} else if (!validString(year) || !validString(month) || !validString(day)) {
			this.dateChanged.emit('');
			return;
		}

		this.checkForErrors(year, month, day);
	}

	/**
	 * Checks for errors with the date selected
	 * @param year The year
	 * @param month The month
	 * @param day The day
	 */
	checkForErrors(year, month, day): void {
		let error: string = null;
		const { datePipe, localeService, outputFormat } = this;
		const date = new Date(toInt(year), toInt(month), toInt(day), 12, 0, 0, 0);
		const selectedDateTime = date.getTime();
		const age = calculateAge(date);
		const minDate = getDateFromInput(this.minDate);
		const maxDate = getDateFromInput(this.maxDate);
		const creditCardDate = new Date();
		creditCardDate.setDate(1);
		creditCardDate.setHours(12, 0, 0, 0);

		if (this.creditCardExpiration && selectedDateTime < creditCardDate.getTime()) {
			error = localeService.get('error.mindate').replace(/@@date/gi, datePipe.transform(minDate, 'mediumDate'));
		} else if (isNumber(this.minAge) && toInt(this.minAge) > 0 && age < this.minAge) {
			error = localeService.get('error.minage').replace(/@@age/gi, '' + this.minAge);
		} else if (isNumber(this.maxAge) && toInt(this.maxAge) > 0 && age > this.maxAge) {
			error = localeService.get('error.maxage').replace(/@@age/gi, '' + this.maxAge);
		} else if (minDate && minDate.getTime() > selectedDateTime) {
			if (validArray(this.use)) {
				error = localeService
					.get('error.mindate')
					.replace(/@@date/gi, datePipe.transform(minDate, this.getFormatForDisplay(this.use, this.format)));
			} else {
				error = localeService
					.get('error.mindate')
					.replace(/@@date/gi, datePipe.transform(minDate, 'mediumDate'));
			}
		} else if (maxDate && maxDate.getTime() < selectedDateTime) {
			if (validArray(this.use)) {
				error = localeService
					.get('error.maxdate')
					.replace(/@@date/gi, datePipe.transform(maxDate, this.getFormatForDisplay(this.use, this.format)));
			} else {
				error = localeService
					.get('error.maxdate')
					.replace(/@@date/gi, datePipe.transform(maxDate, 'mediumDate'));
			}
		}

		Object.keys(this.dateCollector.formGroup.controls).forEach((prop) => {
			this.dateCollector.formGroup.controls[prop].setErrors(
				error === null
					? null
					: {
							age: true,
					  }
			);
		});

		this.error = error;
		const formattedDate = this.datePipe.transform(date, validString(outputFormat) ? outputFormat : 'yyyy-MM-dd');
		this.lastValues.date = formattedDate;
		this.dateChanged.emit(error === null ? formattedDate : '');
	}

	/**
	 * Returns the date to be displayed in errors
	 * @param use If were using just specific inputs
	 * @param format The format to use
	 */
	getFormatForDisplay(use: DateCollectionKeyEnum[], format: string): string {
		return format
			.split('/')
			.map((key) => {
				const normalizedKey = normalize(key);
				if (normalizedKey.includes('m')) {
					if (use.includes(DateCollectionKeyEnum.month)) {
						return 'MMM';
					}
					return '';
				} else if (normalizedKey.includes('d')) {
					if (use.includes(DateCollectionKeyEnum.day)) {
						return 'd';
					}
					return '';
				} else if (use.includes(DateCollectionKeyEnum.year)) {
					return 'y';
				}
				return '';
			})
			.filter(validString)
			.join(', ');
	}
}
